import { StyleSheet } from 'react-native';
import { ACCOUNT_COLORS } from './accountColors';

/**
 * Shared styles for mobile account settings sections
 * 
 * These styles are used across all account section components:
 * - MobileAccountInfoSection
 * - MobilePaymentMethodsSection
 * - MobileAutopaySection
 * - MobileResetPasswordSection
 */
export const accountSectionStyles = StyleSheet.create({
  // ==================== SECTION CONTAINER ====================
  /**
   * Main container for account section content cards
   * Features rounded top corners and border styling per Figma design
   */
  container: {
    flex: 1,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: ACCOUNT_COLORS.innerBorder,
    overflow: 'hidden',
  },

  // ==================== SECTION HEADER ====================
  /**
   * Header bar at top of each section
   */
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ACCOUNT_COLORS.innerBorder,
  },

  /**
   * Section header title text
   * Figma: 18px, semibold, #EEF1F4
   */
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 27,
  },

  // ==================== CONTENT AREA ====================
  /**
   * Standard content padding
   */
  content: {
    paddingHorizontal: 20,
    paddingVertical: 24,
  },

  /**
   * Content with gap between items
   */
  contentWithGap: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 16,
  },

  // ==================== LOADING STATE ====================
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },

  // ==================== EMPTY STATE ====================
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },

  emptyIconContainer: {
    marginBottom: 16,
  },

  /**
   * Figma: 18px semibold for Payment Methods, 16px semibold for Autopay
   * Using 16px as default - override with emptyTitleLarge if needed
   */
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ACCOUNT_COLORS.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },

  emptyTitleLarge: {
    fontSize: 18,
    fontWeight: '600',
    color: ACCOUNT_COLORS.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },

  emptyMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    textAlign: 'center',
    lineHeight: 21,
  },

  // ==================== MODAL STYLES ====================
  /**
   * Full-screen modal container
   */
  modalContainer: {
    flex: 1,
    backgroundColor: ACCOUNT_COLORS.background,
  },

  /**
   * Modal content card with border
   */
  modalContent: {
    flex: 1,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
    borderWidth: 1,
    borderColor: ACCOUNT_COLORS.outerBorder,
    borderRadius: 8,
    margin: 1,
  },

  /**
   * Modal body - centered content area
   */
  modalBody: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    gap: 16,
  },

  /**
   * Modal body with wider padding
   */
  modalBodyWide: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 16,
  },

  /**
   * Modal icon container
   */
  modalIconContainer: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },

  /**
   * Modal icon container - larger variant
   */
  modalIconContainerLarge: {
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },

  /**
   * Modal title text
   */
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: ACCOUNT_COLORS.textPrimary,
    textAlign: 'center',
    lineHeight: 30,
  },

  /**
   * Modal message text
   */
  modalMessage: {
    fontSize: 16,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 327,
  },

  /**
   * Modal message text - smaller variant
   */
  modalMessageSmall: {
    fontSize: 14,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    textAlign: 'center',
    lineHeight: 21,
    maxWidth: 327,
  },

  /**
   * Modal footer with buttons
   */
  modalFooter: {
    flexDirection: 'row',
    gap: 35,
    paddingHorizontal: 24,
    paddingTop: 24,
    justifyContent: 'center',
  },

  // ==================== BUTTON STYLES ====================
  /**
   * Outlined cancel/secondary button
   */
  cancelButton: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: ACCOUNT_COLORS.buttonOutline,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 21,
  },

  /**
   * Primary/confirm button with fill
   */
  confirmButton: {
    flex: 1,
    height: 40,
    backgroundColor: ACCOUNT_COLORS.buttonPrimary,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  confirmButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 21,
  },

  /**
   * Taller button variant (48px height)
   */
  cancelButtonTall: {
    flex: 1,
    height: 48,
    borderWidth: 1,
    borderColor: ACCOUNT_COLORS.buttonOutline,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  confirmButtonTall: {
    flex: 1,
    height: 48,
    backgroundColor: ACCOUNT_COLORS.buttonPrimary,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  confirmButtonDisabled: {
    opacity: 0.6,
  },

  /**
   * Action button with chevron (for "Reset Password", "Add Card", etc.)
   */
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: ACCOUNT_COLORS.buttonOutline,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    height: 40,
  },

  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 21,
  },

  // ==================== DIVIDER ====================
  /**
   * Horizontal divider line
   * Figma: bg-[#464646], h-px
   */
  divider: {
    height: 1,
    backgroundColor: ACCOUNT_COLORS.divider,
    width: '100%',
  },

  // ==================== AVATAR ====================
  /**
   * Avatar container
   * Figma: 68x68, rounded-[34px], bg rgba(238,241,244,0.1)
   */
  avatarContainer: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: ACCOUNT_COLORS.avatarBackground,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  avatarImage: {
    width: 68,
    height: 68,
  },

  avatarInitials: {
    fontSize: 24,
    fontWeight: '700',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 36,
  },
});

export default accountSectionStyles;
